import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const savedUser = localStorage.getItem('freelanders_user');
    const savedToken = localStorage.getItem('freelanders_token');
    
    if (savedUser && savedToken) {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
    
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    // Mock login for now
    setLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const mockUser = {
      id: 'user_123',
      name: email.split('@')[0],
      email: email,
      avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face',
      status: 'New to Freelanders',
      skills: ['Full Stack', 'UI/UX', 'Mobile'],
      location: { lat: 37.7749, lng: -122.4194 }
    };
    
    const mockToken = 'mock_jwt_token_' + Date.now();
    
    localStorage.setItem('freelanders_user', JSON.stringify(mockUser));
    localStorage.setItem('freelanders_token', mockToken);
    
    setUser(mockUser);
    setIsAuthenticated(true);
    setLoading(false);
    
    return { success: true, user: mockUser, token: mockToken };
  };

  const register = async (name, email, password) => {
    // Mock registration for now
    setLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const mockUser = {
      id: 'user_' + Date.now(),
      name: name,
      email: email,
      avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face',
      status: 'New to Freelanders',
      skills: [],
      location: { lat: 37.7749, lng: -122.4194 }
    };
    
    const mockToken = 'mock_jwt_token_' + Date.now();
    
    localStorage.setItem('freelanders_user', JSON.stringify(mockUser));
    localStorage.setItem('freelanders_token', mockToken);
    
    setUser(mockUser);
    setIsAuthenticated(true);
    setLoading(false);
    
    return { success: true, user: mockUser, token: mockToken };
  };

  const loginWithGoogle = async () => {
    // Mock Google login for now
    setLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const mockUser = {
      id: 'google_user_' + Date.now(),
      name: 'Google User',
      email: 'google.user@gmail.com',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      status: 'Connected via Google',
      skills: ['Development', 'Design'],
      location: { lat: 37.7749, lng: -122.4194 }
    };
    
    const mockToken = 'mock_google_token_' + Date.now();
    
    localStorage.setItem('freelanders_user', JSON.stringify(mockUser));
    localStorage.setItem('freelanders_token', mockToken);
    
    setUser(mockUser);
    setIsAuthenticated(true);
    setLoading(false);
    
    return { success: true, user: mockUser, token: mockToken };
  };

  const loginWithApple = async () => {
    // Mock Apple login for now (placeholder)
    setLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const mockUser = {
      id: 'apple_user_' + Date.now(),
      name: 'Apple User',
      email: 'apple.user@icloud.com',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      status: 'Connected via Apple',
      skills: ['iOS', 'Swift'],
      location: { lat: 37.7749, lng: -122.4194 }
    };
    
    const mockToken = 'mock_apple_token_' + Date.now();
    
    localStorage.setItem('freelanders_user', JSON.stringify(mockUser));
    localStorage.setItem('freelanders_token', mockToken);
    
    setUser(mockUser);
    setIsAuthenticated(true);
    setLoading(false);
    
    return { success: true, user: mockUser, token: mockToken };
  };

  const resetPassword = async (email) => {
    // Mock password reset for now
    setLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setLoading(false);
    
    return { success: true, message: 'Password reset link sent to your email' };
  };

  const logout = () => {
    localStorage.removeItem('freelanders_user');
    localStorage.removeItem('freelanders_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    loginWithApple,
    resetPassword,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};